import { useCallback, useMemo, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { retry } from "radash"

import { v4CustomersGeocode } from "@be-miniapp/api"
import { addBreadcrumb, log } from "@be-miniapp/logging"
import {
  isRideLocationCacheExpired,
  isRideLocationCacheFresh,
  shouldRefreshSharedLocationInBackground,
} from "@be-miniapp/shared"
import type { Location } from "@be-miniapp/types"

import { useClientInfo } from "@/store/client-info"
import { useUserPersistingData } from "@/store/user-persisting-data"
import { useUserStore } from "@/store/user-session"
import { getLocationAsync } from "@/utils/location"

import { beUserQueryKeys } from "./use-user"

interface FetchLocationProps {
  options?: {
    onDenied?: () => void
    onAccepted?: () => void
    onUnAvailable?: (message?: string) => void
  }
  forceFresh?: boolean
  providedLocation?: Location | null
}

let sharedLocationRefreshPromise: Promise<unknown> | null = null

function getLocationErrorCode(error: unknown): number | string | undefined {
  if (!error || typeof error !== "object" || !("code" in error)) {
    return undefined
  }

  const code = (error as { code: unknown }).code
  if (typeof code === "number" || typeof code === "string") {
    return code
  }

  return undefined
}

function getLocationErrorMessage(error: unknown): string | undefined {
  if (!error || typeof error !== "object" || !("message" in error)) {
    return undefined
  }

  const message = (error as { message: unknown }).message
  return typeof message === "string" ? message : undefined
}

const useFetchLocation = (props?: FetchLocationProps) => {
  const queryClient = useQueryClient()

  const persistedLocation = useUserPersistingData((state) => state.location)
  const persistedLocationAddress = useUserPersistingData(
    (state) => state.locationAddress
  )
  const setPersistedLocation = useUserPersistingData(
    (state) => state.setLocation
  )
  const setPersistedLocationAddress = useUserPersistingData(
    (state) => state.setLocationAddress
  )
  const clearLocationCache = useUserPersistingData(
    (state) => state.clearLocationCache
  )
  const setLocationPermissionStatus = useUserPersistingData(
    (state) => state.setLocationPermissionState
  )
  const locationPermission = useUserPersistingData(
    (state) => state.locationPermissionState.status
  )
  const lastLocationTimestamp = useUserPersistingData(
    (state) => state.lastLocationTimestamp
  )
  const isLocationManuallyChanged = useUserPersistingData(
    (state) => state.isLocationManuallyChanged
  )

  const setClientLocation = useClientInfo((state) => state.setLocation)

  const setLocation = useUserStore((state) => state.setLocation)
  const setUserAddress = useUserStore((state) => state.setUserAddress)
  const userAddress = useUserStore((state) => state.currentAddress)

  const [isFetchingLocation, setIsFetchingLocation] = useState(false)

  const hydrateLocationCache = useCallback(
    (
      location: Location,
      address: NonNullable<typeof persistedLocationAddress>
    ) => {
      setClientLocation(location, "sdk")
      setLocation(location.latitude, location.longitude)
      setUserAddress(address)
      return address
    },
    [setClientLocation, setLocation, setUserAddress]
  )

  const geocodeAndPersistLocation = useCallback(
    async ({
      location,
      manual = false,
    }: {
      location: Location
      manual?: boolean
    }) => {
      setClientLocation(location, manual ? "manual" : "sdk")
      setLocation(location.latitude, location.longitude)
      setPersistedLocation(location, manual)

      queryClient.invalidateQueries({
        queryKey: beUserQueryKeys.all,
      })

      const addressData = await v4CustomersGeocode({
        latitude: location.latitude,
        longitude: location.longitude,
        location: {
          latitude: location.latitude,
          longitude: location.longitude,
        },
      })

      if (addressData) {
        setPersistedLocationAddress(addressData)
        setUserAddress(addressData)
      }

      return addressData ?? null
    },
    [
      queryClient,
      setClientLocation,
      setLocation,
      setPersistedLocation,
      setPersistedLocationAddress,
      setUserAddress,
    ]
  )

  const fetchFreshLocation = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!silent) {
        setIsFetchingLocation(true)
      }

      let latLng: Location | null = null
      let permissionGranted = false

      try {
        await retry({ times: 1 }, async () => {
          addBreadcrumb({
            category: "location-permission",
            level: "info",
            message: "Fetching location",
            data: { locationPermission, silent },
          })

          const location = await getLocationAsync({
            onTimeout: () => {
              if (silent) return

              addBreadcrumb({
                category: "location-permission",
                level: "error",
                message: "Location fetch timeout",
              })
              setLocationPermissionStatus("denied")
              props?.options?.onUnAvailable?.("timedout")
            },
            providedLocation: props?.providedLocation,
          })

          if (location?.coords) {
            permissionGranted = true
            setLocationPermissionStatus("accepted")
            if (!silent) {
              props?.options?.onAccepted?.()
            }
            latLng = {
              latitude: location.coords.latitude,
              longitude: location.coords.longitude,
            }
          }
        })

        if (!latLng) {
          throw new Error("no latlng found")
        }

        return geocodeAndPersistLocation({
          location: latLng,
          manual: false,
        })
      } catch (err: unknown) {
        const errorCode = getLocationErrorCode(err)

        addBreadcrumb({
          category: "location-permission",
          level: "error",
          message: "Fetch location failed",
          data: err,
        })

        if (permissionGranted) {
          log({
            category: "location-permission",
            level: "warning",
            message: "Location permission granted but follow-up fetch failed",
            data: err,
          })
          return null
        }

        if (!silent) {
          if (errorCode === 1) {
            setLocationPermissionStatus("denied")
            props?.options?.onDenied?.()
            return null
          }

          props?.options?.onUnAvailable?.(
            errorCode ? `code ${errorCode}` : getLocationErrorMessage(err)
          )
        }
      } finally {
        if (!silent) {
          setIsFetchingLocation(false)
        }
      }

      return null
    },
    [
      geocodeAndPersistLocation,
      locationPermission,
      props?.options,
      props?.providedLocation,
      setLocationPermissionStatus,
    ]
  )

  const fetchLocationBeforeRide = useCallback(async () => {
    if (props?.providedLocation) {
      setIsFetchingLocation(true)

      try {
        setLocationPermissionStatus("accepted")
        return await geocodeAndPersistLocation({
          location: props.providedLocation,
          manual: false,
        })
      } catch (error) {
        addBreadcrumb({
          category: "location-permission",
          level: "error",
          message: "Failed to geocode provided location",
          data: { error, location: props.providedLocation },
        })
        return null
      } finally {
        setIsFetchingLocation(false)
      }
    }

    if (
      isLocationManuallyChanged &&
      isRideLocationCacheExpired(lastLocationTimestamp)
    ) {
      clearLocationCache()
      setUserAddress(undefined)
    }

    const cache = {
      location: persistedLocation,
      address: persistedLocationAddress,
      lastLocationTimestamp,
      isLocationManuallyChanged,
    }

    if (
      !props?.forceFresh &&
      persistedLocation &&
      persistedLocationAddress &&
      isRideLocationCacheFresh(cache)
    ) {
      const cachedAddress = hydrateLocationCache(
        persistedLocation,
        persistedLocationAddress
      )

      if (
        shouldRefreshSharedLocationInBackground(cache) &&
        locationPermission === "accepted" &&
        !sharedLocationRefreshPromise
      ) {
        sharedLocationRefreshPromise = fetchFreshLocation({
          silent: true,
        }).finally(() => {
          sharedLocationRefreshPromise = null
        })
      }

      return cachedAddress
    }

    if (
      !props?.forceFresh &&
      userAddress &&
      locationPermission !== "accepted"
    ) {
      return userAddress
    }

    return fetchFreshLocation()
  }, [
    clearLocationCache,
    fetchFreshLocation,
    geocodeAndPersistLocation,
    hydrateLocationCache,
    isLocationManuallyChanged,
    lastLocationTimestamp,
    locationPermission,
    persistedLocation,
    persistedLocationAddress,
    props?.forceFresh,
    props?.providedLocation,
    setLocationPermissionStatus,
    userAddress,
    setUserAddress,
  ])

  return useMemo(
    () => ({
      isFetching: isFetchingLocation,
      fetch: fetchLocationBeforeRide,
    }),
    [fetchLocationBeforeRide, isFetchingLocation]
  )
}

export { useFetchLocation }
